import React from 'react';
import { Form, Input, Select, InputNumber, Slider, ColorPicker, Tabs, Checkbox } from 'antd';
import type { FormInstance } from 'antd';
import type { FormField, FormTab, ItemWithId, AnyFormConfig, StoreState } from '../../types/types';
import type { ModalInternalState } from '../../types/types';

interface EditModalFormProps<T extends ItemWithId> {
  config: AnyFormConfig;
  currentItem: T;
  form: FormInstance;
  internalState: ModalInternalState;
  setInternalState: React.Dispatch<React.SetStateAction<ModalInternalState>>;
  /** Stable reference to useGlobalState.getState, passed from the Container via TypedEditModal */
  getState: () => StoreState;
}

const renderField = (field: FormField, currentItem: ItemWithId, internalState: ModalInternalState, setInternalState: React.Dispatch<React.SetStateAction<ModalInternalState>>, form: FormInstance, getState: () => StoreState) => {
  const currentState = getState();
  const { name, label, type, options, component: CustomComponent, rules, initialValue, ...rest } = field;

  let inputNode: React.ReactNode;
  switch (type) {
    case 'textarea':
      inputNode = <Input.TextArea rows={6} {...rest} />;
      break;
    case 'select': {
      const selectOptions = (typeof options === 'function' ? options(currentItem, currentState) : options) ?? [];
      inputNode = <Select options={selectOptions} {...rest} allowClear />;
      break;
    }
    case 'number':
      inputNode = <InputNumber style={{ width: '100%' }} {...rest} />;
      break;
    case 'color':
      inputNode = <ColorPicker showText style={{ width: '100%' }} {...rest} />;
      break;
    case 'range':
      inputNode = <Slider {...rest} />;
      break;
    case 'checkbox':
      inputNode = <Checkbox {...rest} />;
      break;
    case 'custom':
      // Custom components can now be either controlled (receiving value/onChange from Form.Item)
      // or uncontrolled (managing their own state via internalState).
      // We pass all relevant props and let the component decide what to use.
      if (CustomComponent) {
        inputNode = <CustomComponent item={currentItem} internalState={internalState} setInternalState={setInternalState} form={form} />;
      }
      break;
    default:
      inputNode = <Input {...rest} />;
  }

  return (
    <Form.Item
      key={Array.isArray(name) ? name.join('.') : name.toString()}
      name={name}
      label={label}
      rules={rules}      // For checkboxes, we need to specify which prop holds the value.
      valuePropName={type === 'checkbox' ? 'checked' : 'value'}
      initialValue={typeof initialValue === 'function' ? initialValue(currentState) : initialValue}>
      {inputNode}
    </Form.Item>
  );
};

const EditModalForm = <T extends ItemWithId>({ config, currentItem, form, internalState, setInternalState, getState }: EditModalFormProps<T>) => {
  if (config.tabs) {
    const tabItems = (config.tabs ?? []).map((tab: FormTab) => ({
      key: tab.id,
      label: tab.label,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {(tab.fields ?? []).map((field: FormField) => renderField(field, currentItem, internalState, setInternalState, form, getState))}
        </div>
      ),
    }));
    return <Tabs defaultActiveKey={config.tabs[0]?.id} items={tabItems} />;
  }

  return (
    <>
      {config.fields?.map((field: FormField) =>
        renderField(field, currentItem, internalState, setInternalState, form, getState)
      )}
    </>
  );
};

export default EditModalForm;
